/**
 * Types métier corrosion — Ultra Mesure
 * Spécifique au module corrosion (pas dans core/)
 */

/** Code couleur selon le % de corrosion */
export type CodeCouleur = "green" | "orange" | "red" | "dark";

/** Orientation de la mesure */
export type Orientation = "H" | "V";

/** Données du formulaire de mesure corrosion */
export interface MesureFormData {
  // Identification
  numero: number;
  reseau: string;
  localisation: string;

  // Technique
  diametreExt: number | null;
  dn: number | null; // calculé via table ABAC
  orientation: Orientation;
  materiau: string;
  tarification: string[];

  // Mesures terrain
  mesureMax: number | null;
  mesureMin: number | null;

  // Résultats calculés
  epaisseurOrigine: number | null;
  pourcentageCorrosion: number | null;
  codeCouleur: CodeCouleur | null;

  // Divers
  notes: string;
}

/** Liste des réseaux disponibles (placeholder — à compléter avec Renaud) */
export const RESEAUX = [
  "AEP",
  "Incendie",
  "Eau brute",
  "Refoulement",
  "Distribution",
  "Adduction",
] as const;

// ─── Nomenclature Réseau structurée (select 1 + complément + sens) ────────

/** Types de réseau (select 1) — label affiché = valeur stockée */
export const RESEAU_TYPES = [
  "Eau glacée",
  "Chauffage",
  "Change-over",
  "Eau chaude sanitaire",
  "PAC",
  "DRY",
  "Eau glycolée",
  "Eau froide sanitaire",
  "Sprinkler",
  "RIA",
  "Eau usée",
  "Eau pluviale",
  "Eau vannes",
  "Colonne humide",
  "Gaz",
  "Autres",
] as const;

/** Types de réseau qui déclenchent le 2e select Aller/Retour */
export const RESEAU_TYPES_WITH_SENS: ReadonlyArray<string> = [
  "Eau glacée",
  "Chauffage",
  "Change-over",
  "Eau chaude sanitaire",
  "PAC",
  "DRY",
  "Eau glycolée",
  "Autres",
];

/** Sens (select 2, conditionnel) */
export const RESEAU_SENS = ["Aller", "Retour"] as const;

/** Détermine si un type de réseau nécessite le select Aller/Retour */
export function hasReseauSens(type: string | null | undefined): boolean {
  if (!type) return false;
  return RESEAU_TYPES_WITH_SENS.includes(type);
}

/**
 * Formate un réseau pour l'affichage (PDF, tableau, Excel, nom d'export).
 * Priorité aux champs structurés (type + sens + complément), fallback sur
 * localisation (champ legacy) si aucun type structuré n'est renseigné.
 */
export function formatReseau(m: {
  reseauType?: string | null;
  reseauComplement?: string | null;
  reseauSens?: string | null;
  localisation?: string | null;
}): string {
  const parts: string[] = [];
  if (m.reseauType) parts.push(m.reseauType);
  if (m.reseauSens && hasReseauSens(m.reseauType)) parts.push(m.reseauSens);
  if (m.reseauComplement) parts.push(m.reseauComplement);
  if (parts.length === 0) return m.localisation ?? "";
  return parts.join(" - ");
}

/** Liste des matériaux disponibles (extensible par Renaud) */
export const MATERIAUX = [
  "Cuivre",
  "Fonte",
  "Acier noir",
  "Acier électrozingué",
  "Acier inoxydable",
  "Acier galvanisé",
] as const;

/** Qualifications spécifiques Fonte */
export const FONTE_AGES = ["Vieux", "Récent"] as const;
export const FONTE_TYPES = ["SME", "SMU"] as const;

/** Liste des tarifications — correspond aux normes des tubes acier */
export const TARIFICATIONS = [
  "T1",
  "T19",
  "T3",
  "T10",
  "Hors nomenclature",
] as const;
